import { memo, useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { CalendarCheck, Edit2, Trash2, User, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Button, Input, Label } from '@caruso/ui';
import { useQuery } from '@tanstack/react-query';
import { usersService } from '@/services/users.service';

interface ScheduleCreateData {
  scheduleCreateUserId?: string;
  scheduleEventTitle?: string;
  scheduleEventType?: 'MEETING' | 'APPOINTMENT' | 'TASK' | 'OTHER';
  sendCalendarFile?: boolean;
}

export const ScheduleCreateNode = memo(({ data, id }: NodeProps<ScheduleCreateData>) => {
  const { deleteElements, setNodes } = useReactFlow();
  const [isEditing, setIsEditing] = useState(false);
  const [scheduleCreateUserId, setScheduleCreateUserId] = useState(data.scheduleCreateUserId || '');
  const [scheduleEventTitle, setScheduleEventTitle] = useState(data.scheduleEventTitle || 'Agendamento');
  const [scheduleEventType, setScheduleEventType] = useState<'MEETING' | 'APPOINTMENT' | 'TASK' | 'OTHER'>(data.scheduleEventType || 'APPOINTMENT');
  const [sendCalendarFile, setSendCalendarFile] = useState(data.sendCalendarFile ?? false);

  // Load users
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersService.getAll(),
  });

  const users = usersData || [];

  const handleDelete = () => {
    deleteElements({ nodes: [{ id }] });
  };

  const handleSave = () => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              scheduleCreateUserId,
              scheduleEventTitle,
              scheduleEventType,
              sendCalendarFile,
            },
          };
        }
        return node;
      })
    );
    setIsEditing(false);
  };

  const selectedUser = users.find(u => u.id === scheduleCreateUserId);

  const eventTypeLabels = {
    MEETING: 'Reunião',
    APPOINTMENT: 'Compromisso',
    TASK: 'Tarefa',
    OTHER: 'Outro',
  };

  return (
    <>
      <div className="bg-green-500/10 border-2 border-green-500 rounded-lg p-4 min-w-[280px] shadow-lg">
        <Handle type="target" position={Position.Top} className="!bg-green-500" />

        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
              <CalendarCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-bold text-green-600">Criação de Agendamento</div>
              <div className="text-xs text-gray-500">Confirma e cria o agendamento</div>
            </div>
          </div>

          <div className="flex gap-1">
            <button
              onClick={() => setIsEditing(true)}
              className="p-1.5 hover:bg-green-100 rounded transition-colors"
              title="Editar"
            >
              <Edit2 className="w-4 h-4 text-green-600" />
            </button>
            <button
              onClick={handleDelete}
              className="p-1.5 hover:bg-red-100 rounded transition-colors"
              title="Excluir"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-green-500" />
            <span className="font-medium text-gray-700">
              {selectedUser ? selectedUser.name : 'Nenhum usuário selecionado'}
            </span>
          </div>

          <div className="mt-2 p-2 bg-white/50 rounded text-xs text-gray-600 border border-green-200">
            <div className="flex justify-between">
              <span>Título:</span>
              <span className="font-medium">{scheduleEventTitle}</span>
            </div>
            <div className="flex justify-between mt-1">
              <span>Tipo:</span>
              <span className="font-medium">{eventTypeLabels[scheduleEventType]}</span>
            </div>
            {sendCalendarFile && (
              <div className="flex items-center gap-1 mt-2 text-green-600">
                <FileText className="w-3 h-3" />
                <span>Envia arquivo .ics</span>
              </div>
            )}
          </div>

          {!scheduleCreateUserId && (
            <div className="text-xs text-green-600 italic">
              ⚠️ Configure o usuário para o agendamento
            </div>
          )}
        </div>

        <Handle type="source" position={Position.Bottom} className="!bg-green-500" />
      </div>

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="bg-brand-ink border-brand-gold/20 text-brand-ivory max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-brand-gold flex items-center gap-2">
              <CalendarCheck className="w-5 h-5" />
              Configurar Criação de Agendamento
            </DialogTitle>
            <DialogDescription className="text-brand-ivory/60">
              Configure os parâmetros para criação do agendamento após o usuário selecionar uma opção
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-brand-ivory/80">
                Usuário <span className="text-red-400">*</span>
              </Label>
              <select
                value={scheduleCreateUserId}
                onChange={(e) => setScheduleCreateUserId(e.target.value)}
                className="w-full mt-1 bg-brand-ink/50 border border-brand-gold/20 text-brand-ivory rounded px-3 py-2"
              >
                <option value="">Selecione o usuário...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-brand-ivory/40 mt-1">
                Usuário que receberá o agendamento na agenda
              </p>
            </div>

            <div>
              <Label className="text-brand-ivory/80">
                Título do Evento <span className="text-red-400">*</span>
              </Label>
              <Input
                type="text"
                value={scheduleEventTitle}
                onChange={(e) => setScheduleEventTitle(e.target.value)}
                className="bg-brand-ink/50 border-brand-gold/20 text-brand-ivory mt-1"
                placeholder="Ex: Agendamento com cliente"
              />
              <p className="text-xs text-brand-ivory/40 mt-1">
                Título que aparecerá no evento do calendário
              </p>
            </div>

            <div>
              <Label className="text-brand-ivory/80">
                Tipo de Evento <span className="text-red-400">*</span>
              </Label>
              <select
                value={scheduleEventType}
                onChange={(e) => setScheduleEventType(e.target.value as 'MEETING' | 'APPOINTMENT' | 'TASK' | 'OTHER')}
                className="w-full mt-1 bg-brand-ink/50 border border-brand-gold/20 text-brand-ivory rounded px-3 py-2"
              >
                <option value="APPOINTMENT">Compromisso</option>
                <option value="MEETING">Reunião</option>
                <option value="TASK">Tarefa</option>
                <option value="OTHER">Outro</option>
              </select>
            </div>

            <div className="mt-4 p-4 bg-blue-500/5 rounded border border-blue-500/20">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendCalendarFile}
                  onChange={(e) => setSendCalendarFile(e.target.checked)}
                  className="w-5 h-5 rounded border-brand-gold/30 text-green-500 focus:ring-green-500"
                />
                <div>
                  <div className="flex items-center gap-2 text-brand-ivory">
                    <FileText className="w-4 h-4 text-blue-400" />
                    <span className="font-medium">Enviar arquivo de agendamento (.ics)</span>
                  </div>
                  <p className="text-xs text-brand-ivory/50 mt-1">
                    Envia um arquivo .ics que o cliente pode adicionar ao seu calendário pessoal
                  </p>
                </div>
              </label>
            </div>

            <div className="mt-4 p-3 bg-brand-gold/5 rounded border border-brand-gold/20">
              <p className="text-xs text-brand-ivory/60 mb-2 font-medium">
                Funcionamento:
              </p>
              <ul className="text-xs text-brand-ivory/50 space-y-1 list-disc list-inside">
                <li>Este nó aguarda a resposta do usuário após a consulta de agendamento</li>
                <li>Analisa qual opção (1, 2 ou 3) o usuário escolheu</li>
                <li>Cria o evento de agendamento na agenda do usuário selecionado</li>
                <li>Envia mensagem de confirmação com os detalhes do agendamento</li>
                <li>Se a resposta for inválida, solicita nova resposta</li>
              </ul>
            </div>

            <div className="mt-4 p-3 bg-purple-500/5 rounded border border-purple-500/20">
              <p className="text-xs text-brand-ivory/60 mb-2 font-medium">
                Variáveis disponíveis após execução:
              </p>
              <ul className="text-xs font-mono text-brand-ivory/50 space-y-1">
                <li>{'{{schedule.eventId}}'} - ID do evento criado</li>
                <li>{'{{schedule.startDate}}'} - Data/hora de início</li>
                <li>{'{{schedule.endDate}}'} - Data/hora de término</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button
              variant="outline"
              onClick={() => setIsEditing(false)}
              className="border-brand-gold/20 text-brand-ivory hover:bg-brand-gold/10"
            >
              Cancelar
            </Button>
            <Button
              variant="premium"
              onClick={handleSave}
              disabled={!scheduleCreateUserId || !scheduleEventTitle}
              className="bg-green-500 hover:bg-green-600"
            >
              Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

ScheduleCreateNode.displayName = 'ScheduleCreateNode';
